<template>
  <!-- Мобильный вариант: перетаскиваемый bottom sheet с деталями маршрута вместо
       списка MobileMapOverlay. RouteDetailsPanel.vue работает в компактном режиме
       (сам делает fetch/scroll, кнопки действий и заголовок вынесены сюда), этот
       компонент добавляет drag-to-resize и подгоняет высоту свёрнутого состояния. -->
  <div ref="rootEl" class="md:hidden absolute inset-x-0 bottom-0 z-40 pointer-events-none">
    <div
      class="absolute inset-x-0 bottom-0 bg-card rounded-t-xl border-t border-x border-border shadow-ink-lg overflow-hidden pointer-events-auto flex flex-col"
      :class="!isDragging && 'transition-[height] duration-200 ease-out'"
      :style="{ height: sheetHeight + 'px' }"
      @pointerdown="onSheetPointerDown"
    >
      <!-- Ручка перетаскивания — чисто визуальный элемент; реальная область
           перетаскивания — весь sheet (шапка + эта полоса), т.к. одной узкой
           полоски недостаточно для надёжного захвата пальцем, см. onSheetPointerDown. -->
      <div class="shrink-0 py-1.5 flex flex-col items-center select-none" style="touch-action: none" data-sheet-drag-handle>
        <div class="w-10 h-1 rounded-full bg-[hsl(var(--muted-foreground)/0.3)]" />
      </div>

      <RouteDetailsPanel
        ref="panelRef"
        class="flex-1 min-h-0"
        :selectedRouteId="routeId"
        :cursorDistanceKm="cursorDistanceKm"
        compact
        @close="$emit('close')"
        @back="$emit('back')"
        @show-login="$emit('show-login')"
        @tab-select="expandIfCollapsed"
        @open-profile="$emit('open-profile', $event)"
        @update:cursorDistanceKm="$emit('update:cursorDistanceKm', $event)"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { useDragSheet } from "../composables/useDragSheet";
import RouteDetailsPanel from "./RouteDetailsPanel.vue";

// covered - поверх открыта карточка точки (шторка скрыта v-show в App.vue):
// отступ тулбара карты тогда публикует она, а не эта шторка.
const props = defineProps<{ routeId: number; cursorDistanceKm?: number | null; covered?: boolean }>();
defineEmits<{
  (e: "close"): void;
  (e: "back"): void;
  (e: "show-login"): void;
  (e: "update:cursorDistanceKm", km: number | null): void;
  (e: "open-profile", username: string): void;
}>();

// activeRoute прокидывается на уровень выше (App.vue проверяет, загружена ли
// карточка). Действия с маршрутом живут внутри самой панели.
const rootEl = ref<HTMLElement | null>(null);
const panelRef = ref<InstanceType<typeof RouteDetailsPanel> | null>(null);
const activeRoute = computed(() => panelRef.value?.activeRoute ?? null);

// Высота открытия - сводка панели (заголовок+статистика+«Формат»), низ - одна
// шапка; при выборе другого маршрута (тап по другой линии) - снова компактный
// вид. Перетаскивание и отступ тулбара - общие с шторкой точки (useDragSheet).
const { sheetHeight, isDragging, onSheetPointerDown, collapse, expandIfCollapsed } = useDragSheet({
  bottomGap: 0,
  // Во всю высоту карты (родитель-контейнер), чтобы закрыть кнопки темы/языка.
  getMaxHeight: () => rootEl.value?.offsetParent?.clientHeight ?? 0,
  compactHeight: computed(() => panelRef.value?.compactHeight ?? 0),
  headerHeight: computed(() => panelRef.value?.headerHeight ?? 0),
  resetKey: () => props.routeId,
  active: () => !props.covered,
});

// collapse - App.vue на map-interact (тап по пустой карте / её перетаскивание).
defineExpose({ activeRoute, collapse });
</script>
